import React, { useContext } from 'react';
import styles from './EledoUserProfile.module.scss';
import {useForm, FormProvider} from "react-hook-form";
import {
  EledoAccountGeneralFormModel,
  OperationalNotificationsFormModel,
  PaymentNotificationsFormModel,
  TableFieldIdValidation,
  TableFieldNumericValueValidation,
  TableFieldStringValueValidation
} from "model";
import ReactTooltip from "react-tooltip";
import {
  EledoMainPageTitle, EledoPendingIcon,
  EledoLoader, EledoError,
  EledoFormGenerator, EledoPropertyList, EledoTabs,
} from "components/library";
import classnames from 'classnames';
import { useMutation } from '@apollo/client';
import { EledoTab } from 'components/library/EledoTabs/EledoTabs';
import useEledoAccount, { eledoAccountInput } from 'graphql/hooks/useEledoAccount';
import { EledoAccount, EledoAccountInput, UPDATE_ELEDO_ACCOUNT, UpdateEledoAccountData, UpdateEledoAccountVars } from 'graphql/EledoAccountQueries';
import EledoAccountMembersList from 'components/library/EledoAccountMembersList';
import useEledoAccounting from 'graphql/hooks/useEledoAccounting';
import TabSubscription from 'components/EledoUserProfile/TabSubscription';
import { EledoAccountingInput, UPDATE_ELEDO_ACCOUNTING, UpdateEledoAccountingData, UpdateEledoAccountingVars } from 'graphql/EledoAccountingQueries';
import NotyfContext from 'context/NotyfContext';
import { showNotyf } from 'hooks';


const EledoAccountProfile = () => {
  const { loading, error, eledoAccount } = useEledoAccount()
  const { loading: accountingLoading, error: accountingError, eledoAccounting } = useEledoAccounting()
  const [ updateEledoAccount, { loading: savingAccount, error: savingAccountError } ] = useMutation<UpdateEledoAccountData, UpdateEledoAccountVars>(UPDATE_ELEDO_ACCOUNT,
    { onError(error) { showNotyf(error.message, notyf, "error") },
      onCompleted() { showNotyf("Profile has been updated.", notyf, "success") }
    }
  )
  const [ updateEledoAccounting, {loading: savingAccounting, error: savingAccountingError } ] = useMutation<UpdateEledoAccountingData, UpdateEledoAccountingVars>(UPDATE_ELEDO_ACCOUNTING,
    { onError(error) { showNotyf(error.message, notyf, "error") },
      onCompleted() { showNotyf("Billing profile has been updated.", notyf, "success") }
    }
  )

  const notyf = useContext(NotyfContext)
  
  const resolver = async (values: EledoAccountInput & EledoAccountingInput) => {
    const errors: Record<string, any> = {}

      values?.profile?.properties?.forEach(p => {
        if(!p.key) errors.profile = { type: "validate", message: "Property Name cannot be empty" }
      })

      values?.profile?.counters?.forEach(p => {
        if(!p.key) errors.profile = { type: "validate", message: "Counter Name cannot be empty" }
      })


    return {
      values,
      errors,
    };
  }

  const formHandler = useForm<EledoAccountInput & EledoAccountingInput>({mode: "all", resolver});
  const { formState } = formHandler;
  
  const userChanged = formState.isDirty
  const userValid = formState.isValid
  const saving = savingAccount || savingAccounting
  const savingError = savingAccountError + '\n' + savingAccountingError

  // useEffect(() => {
  //   if(eledoAccount !== undefined){
  //     formHandler.reset()
  //   }
  // }, [formHandler, eledoAccount])

  const handleSave = () => {
    if(userValid && userChanged) {
      const account = formHandler.getValues();

      if(formHandler.getFieldState("billTo").isDirty){
        updateEledoAccounting({ variables: { accounting: { billTo: account.billTo } as EledoAccountingInput}})
          .then((data) => formHandler.reset())
      }
        
      delete account.billTo
      updateEledoAccount({ variables: { account }})
        .then((data) => formHandler.reset())
    }
  };
  return (
    <div>
      <div className={styles.EledoUserProfile} data-testid="EledoUserProfile">
        <div className={styles.TopToolbar}>
          <EledoMainPageTitle
            title="Account Profile"
          />
          <div className={classnames("section", "my-section", styles.ButtonDiv)}>
            <span
              data-tip={userValid ? userChanged ? "" : "Your profile is saved" : "Please check the validity of your input data"}
              data-for="save-invalid-tooltip">
              <button className={classnames("button", "w-button", !userValid && "red")}
                      onClick={handleSave}
                      {...(userChanged && userValid ? {} : {"aria-disabled": "true"})}>
                { saving ?
                    <React.Fragment>
                      <EledoPendingIcon/> Saving...
                    </React.Fragment> :
                    <React.Fragment>
                      <i className="icon-floppy-disk"/> Save
                    </React.Fragment>
                }
              </button>
            </span>
          </div>

          <ReactTooltip
            id="save-invalid-tooltip"
            place="bottom"
            type="dark"
            effect="float"
            multiline={true}
          />
        </div>

        <div className="stretch-vertically">
          <FormProvider {...formHandler}>
            <form>
              <EledoTabs id="userProfileTabs">
                <EledoTab label="General" path="">
                  {!error && !loading && <TabGeneral account={eledoAccount} />}
                </EledoTab>
                <EledoTab label="Subscription" path="subscription">
                  {!accountingError && !accountingLoading && <TabSubscription accounting={eledoAccounting}/> }
                </EledoTab>
                <EledoTab label="Properties" path="properties">
                  <TabProperties account={eledoAccount} />
                </EledoTab>
                <EledoTab label="Counters" path="counters">
                  <TabCounters account={eledoAccount} />
                </EledoTab>
                <EledoTab label="Notifications" path="notifications">
                  {!error && !loading && <TabNotifications account={eledoAccount}/>}
                </EledoTab>
                <EledoTab label="Account termination" path="termination">
                  {!error && !loading && <TabAccountTermination account={eledoAccount}/>}
                </EledoTab>
              </EledoTabs>
              { loading && <div className="flex-center"><EledoLoader/></div>}
              { error && 
                <EledoError title={error?.message}
                  description={(<React.Fragment>
                    We are sorry &#9785; We could not load your user data. <br/><br/>
                    {(error?.message ? error.message.toLocaleLowerCase() : error) === "network error" && "Please check your internet connection."}
                  </React.Fragment>)}/>
              }
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}

const TabGeneral = ({ account }: {account: EledoAccount | undefined}) => {

  if(account === undefined){
    return (<></>)
  }

  const profile = account?.profile

  return (
    <div>
      <EledoFormGenerator
        formModel={EledoAccountGeneralFormModel}
        editedDataObject={eledoAccountInput(account)}
      />
      <h3>Members</h3>
      <EledoAccountMembersList />
    </div>
  );
};

const TabProperties = ({ account }: {account: EledoAccount | undefined}) => {

  if(account === undefined){
    return (<></>)
  }

  const profile = account?.profile

  return (
    <div>
      <h3>Properties</h3>
      <p>Properties can be refered in data expressions of your document templates using '$' prefix and a property name, e.g. '$CompanyName'. It helps you to organize and maintain your global values in one place and change across your templates easily.</p>
      {profile?.properties && <EledoPropertyList
        defaultValues={profile.properties}
        fields={[
          {id: "key", label: "Property Name", type: "text", validation: TableFieldIdValidation},
          {id: "value", label: "Property Value", type: "text", validation: TableFieldStringValueValidation}
        ]}
        path="profile.properties"
      />}

    </div>
  );
};

const TabCounters = ({ account }: {account: EledoAccount | undefined}) => {

  if(account === undefined){
    return (<></>)
  }

  const profile = account?.profile

  return (
    <div>
      <h3>Counters</h3>
      <p>Counters can be refered in data expressions of your document templates using '#' prefix and a counter name, e.g. '#InvoiceNumber'. It allows you to auto number your generated documents.</p>
      {profile?.counters && <EledoPropertyList
        defaultValues={profile.counters}
        fields={[
          {id: "key", label: "Counter Name", type: "text", validation: TableFieldIdValidation},
          {id: "value", label: "Counter Value", type: "number", validation: TableFieldNumericValueValidation},
        ]}
        path="profile.counters"
      />}

    </div>
  );
};

const TabNotifications = ({ account }: {account: EledoAccount | undefined}) => {
  if(account === undefined){
    return (<></>)
  }

  const notifications = account?.notifications

  return (
    <div>
      <h3>Operational notifications</h3>
      {notifications && <EledoFormGenerator
        formModel={OperationalNotificationsFormModel}
        editedDataObject={account}
      />}
      <h3>Payment notifications</h3>
      {notifications && <EledoFormGenerator
        formModel={PaymentNotificationsFormModel}
        editedDataObject={account}
      />}
    </div>
  );
};

const TabAccountTermination = ({ account }: {account: EledoAccount | undefined}) => {
  // const {status, error, closeAccount, cancelCloseAccount}
  //   = useSelector(state => state.user.termination);
  // const dispatch = useDispatch();

  // useEffect(() => {
  //   if(status === null) {
  //     dispatch(wasCloseAccountRequestedAction());
  //   }
  // }, [dispatch, status]);


  // useErrorNotyf(error);
  // useErrorNotyf(closeAccount.error);
  // useErrorNotyf(cancelCloseAccount.error);

  // const handleCloseAccount = e => {
  //   e.preventDefault();
  //   dispatch(closeAccountAction());
  // }

  // const handleCancelTermination = e => {
  //   e.preventDefault();
  //   dispatch(cancelCloseAccountAction());
  // }

  return (
    <div>
      {/* <h3>Account termination</h3>
      <p style={{marginBottom: "1rem"}}>
        { status === true &&
          "Your account termination is being reviewed."}
        { status === false &&
          "You can get your account deleted by requesting its termination. " +
          "Your account will be deleted after a confirmation from the Eledo team."
        }
      </p>
      { status === true &&
        <button
          className="button w-button"
          onClick={e => handleCancelTermination(e)}>
          {cancelCloseAccount.loading}
          <EledoPendingContent state={cancelCloseAccount.loading}
                               content="Cancel Account Termination"
                               pendingContent="Cancelling..."/>
        </button>}
      { status === false &&
        <button
          className="button w-button red"
          onClick={e => handleCloseAccount(e)}>
          <EledoPendingContent state={closeAccount.loading}
                               content="Close my Account"
                               pendingContent="Closing your account..."/>
        </button>
      } */}
    </div>
  );
};

export default EledoAccountProfile